import { useMemo, useState } from 'react';
import RedemptionFilters from './components/RedemptionFilters';
import RedemptionTable from './components/RedemptionTable';
import DecisionModal from './components/DecisionModal';
import KpiCard from '../components/KpiCard';
import { redemptions as seedRedemptions } from '@/mocks/rewards';

import type { Redemption } from '@/types/reward';
type DecisionType = 'approve' | 'reject' | 'fulfill';

export default function RedemptionsPage() {
  const [redemptions, setRedemptions] = useState<Redemption[]>(seedRedemptions);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('All');
  const [decision, setDecision] = useState<{ type: DecisionType; redemption: Redemption } | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return redemptions.filter((item) => {
      const matchesQuery = !q || item.member.toLowerCase().includes(q) || item.reward.toLowerCase().includes(q);
      const matchesStatus =
        status === 'All' || item.status === status.toLowerCase();
      return matchesQuery && matchesStatus;
    });
  }, [query, status, redemptions]);

  const pendingCount = redemptions.filter((item) => item.status === 'pending').length;
  const approvedCount = redemptions.filter((item) => item.status === 'approved').length;
  const fulfilledCount = redemptions.filter((item) => item.status === 'fulfilled').length;
  const pointsPending = redemptions
    .filter((item) => item.status === 'pending')
    .reduce((sum, item) => sum + item.pointsSpent, 0);

  const stats = [
    {
      id: 'pending',
      label: 'Pending review',
      value: pendingCount.toString(),
      icon: 'ri-time-line',
      tone: 'secondary' as const,
      delta: pendingCount > 0 ? 'Needs action' : undefined,
      trend: pendingCount > 0 ? ('flat' as const) : undefined,
      note: 'Awaiting admin decision',
    },
    {
      id: 'approved',
      label: 'Approved requests',
      value: approvedCount.toString(),
      icon: 'ri-check-line',
      tone: 'primary' as const,
      note: 'Queued for distribution',
    },
    {
      id: 'fulfilled',
      label: 'Fulfilled rewards',
      value: fulfilledCount.toString(),
      icon: 'ri-check-double-line',
      tone: 'accent' as const,
      note: 'Successfully handed out',
    },
    {
      id: 'points',
      label: 'Points in escrow',
      value: pointsPending.toLocaleString(),
      icon: 'ri-coins-line',
      tone: 'background' as const,
      note: 'Reserved pending points',
    },
  ];

  const confirmDecision = (note: string) => {
    if (!decision) return;
    const { type, redemption } = decision;
    const statusMap: Record<DecisionType, string> = {
      approve: 'approved',
      reject: 'rejected',
      fulfill: 'fulfilled',
    };
    setRedemptions((prev) =>
      prev.map((item) =>
        item.id === redemption.id ? { ...item, status: statusMap[type] as Redemption['status'], note: note || item.note } : item,
      ),
    );
    setDecision(null);
  };

  const openDecision = (type: DecisionType, redemption: Redemption) => {
    setDecision({ type, redemption });
  };

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading font-extrabold text-2xl text-foreground-950 tracking-tight">Redemptions</h3>
          <p className="text-sm text-foreground-500 mt-0.5">Review, approve, and fulfill member redemption requests.</p>
        </div>
        {pendingCount > 0 && (
          <span className="whitespace-nowrap inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/10 text-amber-800 border border-amber-500/20 text-xs font-bold shadow-xs">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
            </span>
            {pendingCount} awaiting approval
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <KpiCard
            key={stat.id}
            label={stat.label}
            value={stat.value}
            icon={stat.icon}
            tone={stat.tone}
            delta={stat.delta}
            trend={stat.trend}
            note={stat.note}
          />
        ))}
      </div>

      <RedemptionFilters
        query={query}
        onQueryChange={setQuery}
        status={status}
        onStatusChange={setStatus}
        resultCount={filtered.length}
        total={redemptions.length}
      />

      <RedemptionTable
        data={filtered}
        onApprove={(r) => openDecision('approve', r)}
        onReject={(r) => openDecision('reject', r)}
        onFulfill={(r) => openDecision('fulfill', r)}
      />

      {decision && (
        <DecisionModal
          open={!!decision}
          type={decision.type}
          redemption={decision.redemption}
          onConfirm={confirmDecision}
          onClose={() => setDecision(null)}
        />
      )}
    </div>
  );
}
